import set from "lodash/set";
import isEmpty from "lodash/isEmpty";
import escape from "lodash/escape";
import shuffle from "lodash/shuffle";
import { getValueOrLabelByFieldId } from "@/helpers/utils";

const sharedActions = {
  props: {
    styling: {
      type: Object,
      required: true,
    },
    questionNumber: {
      type: Number,
      default: 0,
    },
    formElements: {
      type: Array,
      required: true,
    },
    variables: {
      type: Array,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    hasMultipleFields: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    allowedRoleIds: {
      type: Array,
      default: () => [],
    },
    allowAllFields: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["next", "form-value-changed"],
  data() {
    return {
      timeout: null,
    };
  },
  computed: {
    label() {
      const { isHidden, label, htmlLabel } = this.item;

      if (!this.showLabel) return "";

      let question = this.replaceTagWithAnswer(htmlLabel || label);

      const showQuestionNumber =
        this.questionNumber && this.styling.showQuestionNumber && !isHidden;

      return showQuestionNumber
        ? `Q${this.questionNumber}. ${question}`
        : question;
    },
    description() {
      if (!this.showLabel) return;

      return this.replaceTagWithAnswer(this.item.description);
    },
    correctOptions() {
      return this.item.options.filter(
        (option) =>
          this.item.quizScore.hasOwnProperty(option.id) &&
          this.item.quizScore[option.id].value > 0,
      );
    },
    correctAnswers() {
      return this.correctOptions.map((option) => option.id);
    },
    correctAnswerLabels() {
      return this.correctOptions.map((option) => option.label);
    },
    revealAnswer: {
      get() {
        return (
          this.item.isQuizField &&
          !this.editing &&
          this.hasPositiveScores &&
          this.styling.showFeedback &&
          this.item.revealAnswer
        );
      },
      set(value) {
        set(this.item, "revealAnswer", value);
      },
    },
    isEditingDisabled() {
      return this.revealAnswer && !this.item.allowEditing;
    },
    revealAnswerStyle() {
      return {
        color: this.styling.fontColor || "#575757",
        "user-select": "none",
        "margin-top": "8px",
      };
    },
    showAnswerText() {
      return (
        !this.editing &&
        this.item.isQuizField &&
        this.hasPositiveScores &&
        this.styling.showFeedback &&
        !isEmpty(this.item.value) &&
        !this.revealAnswer
      );
    },
    hasPositiveScores() {
      // Check if any options has score more than 0 (correct answer)
      return Object.values(this.item.quizScore).some(
        (values) => values.value > 0,
      );
    },
    isDisabled() {
      // Check for property called hasFieldAssignee in item
      if (this.item.hasOwnProperty("hasFieldAssignee")) {
        if (!this.item.hasFieldAssignee) return false;

        if (this.allowAllFields) return false;

        const fieldRoleId = parseInt(this.item.fieldRoleId);
        if (!fieldRoleId) return true;

        return !this.allowedRoleIds.includes(fieldRoleId);
      }
      return false;
    },
    shuffledOptions() {
      if (!this.item.hasOwnProperty("options")) return [];
      if (
        this.editing ||
        (!this.styling.allowRandomizeOptions &&
          !this.item.allowRandomizeOptions)
      ) {
        return this.item.options;
      } else {
        return shuffle(this.item.options);
      }
    },
  },
  watch: {
    "item.value": {
      handler() {
        if (this.item.componentFromRepeatField) return;

        this.$emit("form-value-changed", this.item);
      },
      deep: true,
    },
    "item.customOptionValue": {
      handler() {
        this.$emit("form-value-changed", this.item);
      },
    },
    "item.children": {
      handler() {
        this.$emit("form-value-changed", this.item);
      },
      deep: true,
    },
  },
  methods: {
    autoProgressToNext() {
      if (
        this.styling.multiPage &&
        !this.hasMultipleFields &&
        this.styling.autoProgression &&
        !this.styling.showFeedback
      ) {
        if (this.timeout) {
          // clear previous timeout if not executed yet
          clearTimeout(this.timeout);
          this.timeout = null;
        }
        this.timeout = setTimeout(() => {
          this.$emit("next");
          this.timeout = null;
        }, 750);
      }
    },
    replaceTagWithAnswer(question) {
      const regex = /@\{(.*?)\}/;
      let matches = question?.match(regex);

      while (matches?.length > 0) {
        if (matches[1].startsWith("variable_")) {
          matches[1] = matches[1].split("_")[1];
        }

        question = question.replace(
          matches[0],
          this.editing
            ? this.createInformationRecallBadge(matches[1])
            : this.retrieveFieldData(matches[1]),
        );
        matches = question.match(regex);
      }

      return question;
    },
    createInformationRecallBadge(id) {
      const value = getValueOrLabelByFieldId({
        id,
        items: this.formElements,
        variables: this.variables,
      });
      return this.createStyledBadge(value);
    },
    createStyledBadge(value) {
      return `<span style="background: ${this.styling.buttonColor || "#00A3FF"};
          border-radius: 8px;
          color: ${this.styling.buttonTextColor || "white"};
          margin: 2px;
          padding: 2px 6px;"
        >
          ${value}
        </span>`;
    },
    retrieveFieldData(id) {
      const value = getValueOrLabelByFieldId({
        id,
        items: this.formElements,
        variables: this.variables,
        outputType: "only-value",
      });
      return escape(value);
    },
    processInputValue(value) {
      const regex = /@{([^}]+)}/g;
      const regExToGetNumber = /\d+(\.\d+)?/;
      let processedValue = value.toString();
      let matches = processedValue.match(regex);

      while (matches?.length > 0) {
        const match = matches[0]; // Get the full match including '@{}'
        let matchWithoutSymbol = match.slice(2, -1); // Remove the '@{' and '}' symbols

        if (matchWithoutSymbol.startsWith("variable_")) {
          //get variable uuid if matchWithoutSymbol is variable
          matchWithoutSymbol = matchWithoutSymbol.split("_")[1];
        }

        let processedMatch = this.retrieveFieldData(matchWithoutSymbol); // Get the processed value for the match
        let processedMatchToNumber = 0;

        // conver the value to Array and add each value to get final value
        processedMatch.split(",").forEach((value) => {
          // get the number from the value ex: 10% => 10, Product 1 - $5.5 => 5.5, 1 => 1
          const matched = value.match(regExToGetNumber);
          const number = matched ? Number(matched[0]) : 0;
          processedMatchToNumber += number;
        });

        processedValue = processedValue.replace(match, processedMatchToNumber);
        matches = processedValue.match(regex);
      }
      return processedValue;
    },
  },
};

export default sharedActions;
